import * as React from "react";
import { DayPicker } from "react-day-picker";
import { ChevronLeft, ChevronRight } from "lucide-react";

import "@/styles/components/calendar.css";

import { cn } from "@/lib/utils";

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("p-3", className, "w-max-[328px]")}
      classNames={{
        months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
        month: "space-y-4",
        month_caption: "flex justify-center pt-1 relative items-center",
        caption_label: "calendar-caption-label",
        nav: "space-x-1 flex items-center absolute inset-x-3 top-4 justify-between z-10",
        button_previous: cn(
          "text-ui-tbd3 h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 inline-flex items-center justify-center",
        ),
        button_next: cn(
          "text-ui-tbd3 h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 inline-flex items-center justify-center",
        ),
        month_grid: "w-full border-collapse space-y-1",
        weekdays: "flex gap-[8px]",
        weekday:
          "text-muted-foreground rounded-md w-[30px] calendar-dow self-center",
        week: "flex w-full mt-2 gap-[8px]",
        day: "calendar-date h-[30px] w-[30px] text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-lg focus-within:relative focus-within:z-20",
        // The shared `buttonVariants({ variant: "ghost" })` we used here before
        // baked in `text-white` from the button base, which left the day
        // numbers unreadable against the orange `selected` modifier and faint
        // on hover. Inline a calendar-specific button style instead.
        day_button: cn(
          "inline-flex items-center justify-center rounded-full cursor-pointer transition-colors",
          "h-[30px] w-[30px] p-0 font-normal text-ui-coal",
          "hover:bg-cta-opacity-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          "aria-selected:opacity-100",
        ),
        range_end: "day-range-end",
        selected: "calendar-date-selected",
        today: "border border-cta rounded-full",
        outside:
          "day-outside text-muted-foreground aria-selected:bg-accent/50 aria-selected:text-muted-foreground",
        disabled: "text-muted-foreground opacity-50",
        range_middle:
          "aria-selected:bg-accent aria-selected:text-accent-foreground",
        hidden: "invisible",
        ...classNames,
      }}
      components={{
        Chevron: ({ className, orientation, ...props }) =>
          orientation === "left" ? (
            <ChevronLeft className={cn("h-4 w-4", className)} {...props} />
          ) : (
            <ChevronRight className={cn("h-4 w-4", className)} {...props} />
          ),
      }}
      modifiersStyles={{
        selected: {
          backgroundColor: "#2973DB", // theme `cta`
          color: "#FFFFFF",
          borderRadius: "29px",
        },
      }}
      {...props}
    />
  );
}

Calendar.displayName = "Calendar";

export { Calendar };
